import type { SuggestionItem, SuggestionProvider } from "../../core/suggestions";
import type { DocumentId } from "../../core/types";
import type { TriggerMatch, TriggerRule } from "./trigger";

/** A trigger match tied to where it is, so "the same place" can be told from "the same text". */
export interface ActiveMatch extends TriggerMatch {
  /** Stable while the user keeps typing the same query: the text node and the trigger's offset. */
  id: string;
}

export type SuggestionStatus = "loading" | "ready" | "error";

export interface SuggestionState {
  open: boolean;
  trigger: string;
  query: string;
  items: readonly SuggestionItem[];
  status: SuggestionStatus;
  activeIndex: number;
}

export interface SuggestionEngineOptions {
  providers: readonly SuggestionProvider[];
  documentId: DocumentId;
  /** Quiet time before a remote search starts. Local results never wait. */
  debounceMs?: number;
  /**
   * Whatever scopes what this user may see (a workspace, an account). Part of every cache key, and
   * read per request, so results fetched in one context are never shown in another.
   */
  cacheContext?: () => string;
}

export interface SuggestionEngine {
  readonly rules: readonly TriggerRule[];
  subscribe(listener: () => void): () => void;
  getState(): SuggestionState;
  /** Tells the engine what trigger, if any, the caret is in. Call after every editor change. */
  update(match: ActiveMatch | null): void;
  move(delta: number): void;
  setActive(index: number): void;
  /** Closes the menu and keeps it closed for this match. The typed text is left alone. */
  dismiss(): void;
  /** The match and item an accept would act on, to be re-validated against the document. */
  current(): { match: ActiveMatch; item: SuggestionItem } | null;
  dispose(): void;
}

const CLOSED: SuggestionState = {
  open: false,
  trigger: "",
  query: "",
  items: [],
  status: "ready",
  activeIndex: 0,
};
const MAX_CACHE_ENTRIES = 200;

const isAbort = (error: unknown): boolean =>
  typeof error === "object" && error !== null && "name" in error && error.name === "AbortError";

function merge(lists: readonly (readonly SuggestionItem[])[]): SuggestionItem[] {
  const seen = new Set<string>();
  const merged: SuggestionItem[] = [];
  for (const list of lists) {
    for (const item of list) {
      if (seen.has(item.key)) continue;
      seen.add(item.key);
      merged.push(item);
    }
  }
  return merged;
}

export function createSuggestionEngine(options: SuggestionEngineOptions): SuggestionEngine {
  const { providers, documentId, debounceMs = 120, cacheContext = () => "" } = options;
  const listeners = new Set<() => void>();
  const cache = new Map<string, readonly SuggestionItem[]>();
  let state = CLOSED;
  let match: ActiveMatch | null = null;
  let dismissed: string | null = null;
  let request = 0;
  let controller: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const rules: TriggerRule[] = providers.flatMap((provider) =>
    provider.triggers.map((trigger) => ({
      trigger,
      allowSpaces: provider.allowSpaces ?? true,
      minQueryLength: provider.minQueryLength ?? 0,
      blockStartOnly: provider.blockStartOnly ?? false,
    })),
  );

  const set = (next: SuggestionState): void => {
    state = next;
    for (const listener of listeners) listener();
  };

  const cancel = (): void => {
    request++;
    controller?.abort();
    controller = null;
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const cacheKey = (provider: SuggestionProvider, active: ActiveMatch): string =>
    [cacheContext(), provider.id, active.trigger, active.query].join("\u0000");

  const remember = (key: string, items: readonly SuggestionItem[]): void => {
    if (cache.size >= MAX_CACHE_ENTRIES) cache.delete(cache.keys().next().value!);
    cache.set(key, items);
  };

  function search(active: ActiveMatch, sources: readonly SuggestionProvider[]): void {
    const local = sources.map((provider) => provider.local?.(active.query, active.trigger) ?? []);
    const remote = sources.filter((provider) => provider.search !== undefined);
    const cached = remote.map((provider) => cache.get(cacheKey(provider, active)));
    const pending = remote.filter((_provider, index) => cached[index] === undefined);
    const known = merge([...local, ...cached.map((items) => items ?? [])]);
    set({
      open: true,
      trigger: active.trigger,
      query: active.query,
      items: known,
      status: pending.length === 0 ? "ready" : "loading",
      activeIndex: 0,
    });
    if (pending.length === 0) return;

    const mine = ++request;
    const abort = new AbortController();
    controller = abort;
    // Keyed by the context the request was made in: if the workspace changes while it is in
    // flight, its answer belongs to the old one and must not be shown for the new.
    const keys = pending.map((provider) => cacheKey(provider, active));
    timer = setTimeout(() => {
      timer = null;
      const results = pending.map((provider, index) => {
        let answer: Promise<readonly SuggestionItem[]>;
        try {
          answer = provider.search!({
            query: active.query,
            trigger: active.trigger,
            documentId,
            signal: abort.signal,
          });
        } catch (error) {
          // A provider that throws instead of rejecting fails the same way.
          answer = Promise.reject(error);
        }
        return answer.then((items) => {
          remember(keys[index]!, items);
          return items;
        });
      });
      void Promise.allSettled(results).then((settled) => {
        // A newer query, a dismissal, or a closed menu has moved on: this answer is stale.
        if (mine !== request) return;
        const failed = settled.some(
          (result) => result.status === "rejected" && !isAbort(result.reason),
        );
        const found = settled.map((result) => (result.status === "fulfilled" ? result.value : []));
        const items = merge([...local, ...cached.map((list) => list ?? []), ...found]);
        set({
          ...state,
          items,
          status: failed ? "error" : "ready",
          activeIndex: Math.min(state.activeIndex, Math.max(items.length - 1, 0)),
        });
      });
    }, debounceMs);
  }

  return {
    rules,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getState: () => state,
    update(next) {
      if (next === null) {
        dismissed = null;
        if (match === null && !state.open) return;
        match = null;
        cancel();
        return set(CLOSED);
      }
      if (next.id === dismissed) return;
      dismissed = null;
      if (match !== null && match.id === next.id && match.query === next.query) return;
      match = next;
      cancel();
      search(
        next,
        providers.filter((provider) => provider.triggers.includes(next.trigger)),
      );
    },
    move(delta) {
      if (!state.open || state.items.length === 0) return;
      const count = state.items.length;
      set({ ...state, activeIndex: (state.activeIndex + delta + count) % count });
    },
    setActive(index) {
      if (state.open && index !== state.activeIndex && index >= 0 && index < state.items.length) {
        set({ ...state, activeIndex: index });
      }
    },
    dismiss() {
      if (match === null) return;
      dismissed = match.id;
      match = null;
      cancel();
      set(CLOSED);
    },
    current() {
      const item = state.items[state.activeIndex];
      return state.open && match !== null && item !== undefined ? { match, item } : null;
    },
    dispose() {
      cancel();
      listeners.clear();
    },
  };
}
